import { Button } from 'module/ui/components/atoms/button';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Label01, SingleLineEllipsis } from 'module/ui/components/styles';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCalendar, faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { PageTemplateLite, PageType } from 'api/page-template/types';
import { snakeCase } from 'lodash';
import { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const Heading = styled.div`
  ${Label01};
`;
const ListLabel = styled.div`
  ${SingleLineEllipsis};
`;
const ListContent = styled(CenterAlignedBox).attrs({
  gap: 4,
})`
  ${FontAwesomeIcon} {
    color: var(--icon-secondary);
  }
`;
const ListItem = styled(CenterAlignedBox).attrs({
  gap: 8,
})`
  padding: var(--spacing-01) var(--spacing-03);
  height: 32px;
  border-bottom: 1px solid var(--divider-default);
  ${ListContent} {
    flex-grow: 0;
    overflow: hidden;
    width: 100%;
  }
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;
const iconForPageType = (pageType: PageType) => {
  switch (pageType) {
    case PageType.Meeting:
      return (
        <FontAwesomeIcon
          icon={faCalendar}
          size={16}
          iconSize={12}
          color="transparent"
        />
      );
    case PageType.Email:
      return (
        <FontAwesomeIcon
          icon={faEnvelope}
          size={16}
          iconSize={12}
          color="transparent"
        />
      );
  }
};

interface Props {
  sources: PageTemplateLite[];
  removeSource?: (source: PageTemplateLite) => void;
}
export const PageTemplateSourceList: FC<Props> = ({
  sources,
  removeSource,
}) => {
  const sourcesByPageType = useMemo(() => {
    return sources.reduce(
      (result, source) => {
        const previous = result[source.pageType] ?? [];

        return {
          ...result,
          [source.pageType]: [...previous, source],
        };
      },
      {} as Record<PageType, PageTemplateLite[]>,
    );
  }, [sources]);

  return (
    <>
      {Object.entries(sourcesByPageType).map(([pageType, sources]) => (
        <Section key={pageType}>
          <Heading>
            <FormattedMessage
              id={`page_template.type.${snakeCase(pageType)}.label`}
            />
          </Heading>
          <List>
            {sources.map((source) => (
              <ListItem key={source.templateId}>
                <ListContent>
                  {iconForPageType(source.pageType)}
                  <ListLabel>{source.templateName}</ListLabel>
                </ListContent>
                {removeSource && (
                  <Button
                    type="text"
                    icon={<FontAwesomeIcon icon={faXmark} size={12} />}
                    onClick={() => removeSource(source)}
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Section>
      ))}
    </>
  );
};
